import { CartAddEvent } from '@theme/events';

/**
 * <multi-compra>
 *
 * Caixa de compra com opções selecionáveis, cada uma podendo exigir a
 * escolha de uma variante por unidade.
 *
 * Usa o mesmo contrato de carrinho do assets/custom-bundle.js do tema:
 * POST em /cart/add.js (formato bulk, FormData) -> GET /cart.js ->
 * dispara CartAddEvent, que cart-icon.js, cart-drawer.js e
 * component-cart-items.js já escutam.
 */
class MultiCompra extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.cards = Array.from(this.querySelectorAll('[data-mc-option]'));
    if (!this.cards.length) return;

    this.priceNow = this.querySelector('[data-mc-now]');
    this.priceCompare = this.querySelector('[data-mc-compare]');
    this.priceOff = this.querySelector('[data-mc-off]');
    this.priceUnit = this.querySelector('[data-mc-unit]');
    this.errorEl = this.querySelector('[data-mc-error]');

    this.addBtn = this.querySelector('[data-mc-add]');
    this.buyBtn = this.querySelector('[data-mc-buy]');
    this.addText = this.querySelector('[data-mc-add-text]');
    this.buyText = this.querySelector('[data-mc-buy-text]');

    this.labels = {
      add: this.dataset.labelAdd || 'Adicionar à sacola',
      buy: this.dataset.labelBuy || 'Comprar agora',
      incomplete: this.dataset.labelIncomplete || 'Selecione os tons',
      added: this.dataset.labelAdded || 'Adicionado',
      loading: this.dataset.labelLoading || 'Adicionando...',
    };

    this.addUrl = this.dataset.addUrl || '/cart/add.js';
    this.cartUrl = this.dataset.cartUrl || '/cart';
    this.propertyName = this.dataset.property || '';
    this.preselect = this.dataset.preselect === 'true';
    this.initialTone = this.dataset.initialTone || '';

    /** @type {Map<string, Array<{id: string, title: string} | null>>} */
    this.picks = new Map();
    this.current = null;
    this.busy = false;

    this.cards.forEach((card) => {
      const tones = Number(card.dataset.tones || 0);
      this.picks.set(card.dataset.mcOption, new Array(tones).fill(null));

      card.querySelector('[data-mc-select]')?.addEventListener('click', () => this.select(card));

      card.querySelectorAll('[data-mc-swatch]').forEach((swatch) => {
        swatch.addEventListener('click', () => {
          this.pick(card, Number(swatch.dataset.unit), swatch.dataset.variantId, swatch.dataset.variantTitle, swatch);
        });
      });

      card.querySelectorAll('[data-mc-variant-select]').forEach((select) => {
        select.addEventListener('change', () => {
          const option = select.options[select.selectedIndex];
          this.pick(card, Number(select.dataset.unit), select.value, option ? option.textContent.trim() : '', null);
        });
      });

      if (this.preselect) this.preselectCard(card);
    });

    this.select(this.cards.find((card) => card.dataset.default === 'true') || this.cards[0]);

    // Sem isso a galeria mostra as fotos de todas as cores ao mesmo tempo
    // (a filtragem nativa só roda dentro do variant-picker, que este
    // bloco substitui). Filtra já na carga pra página não nascer gigante.
    if (this.initialTone) this.filterGallery(this.initialTone);

    this.addBtn?.addEventListener('click', () => this.submit(false));
    this.buyBtn?.addEventListener('click', () => this.submit(true));
  }

  /**
   * Réplica do filterGalleryByAlt() do assets/variant-picker.js do tema.
   * Mostra só os slides cujo <img alt> bate com a cor escolhida; esconde
   * os demais. Nunca mexe no carrossel de vídeos. Imagens sem alt (fotos
   * institucionais, tabela de medidas) continuam sempre visíveis.
   * @param {string} colorValue
   */
  filterGallery(colorValue) {
    if (!colorValue) return;
    const target = colorValue.toLowerCase().trim();
    const container = this.closest('.shopify-section') || document;

    let firstMatch = null;

    container.querySelectorAll('img').forEach((img) => {
      if (img.closest('video-carousel')) return;

      const slide = img.closest(
        'slideshow-slide, .product-media-gallery__grid li, .thumbnail-list__item, .product__media-item, li'
      );
      if (!slide) return;

      const alt = (img.getAttribute('alt') || '').toLowerCase().trim();

      if (alt === target) {
        slide.style.setProperty('display', '', 'important');
        if (!firstMatch && slide.tagName.toLowerCase() === 'slideshow-slide') firstMatch = slide;
      } else if (alt !== '') {
        slide.style.setProperty('display', 'none', 'important');
      } else {
        slide.style.setProperty('display', '', 'important');
      }
    });

    if (firstMatch && typeof firstMatch.scrollIntoView === 'function') {
      setTimeout(() => firstMatch.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'start' }), 30);
    }
  }

  preselectCard(card) {
    card.querySelectorAll('[data-mc-unit]').forEach((unitEl) => {
      const first = unitEl.querySelector('[data-mc-swatch]');
      if (first) {
        this.pick(card, Number(first.dataset.unit), first.dataset.variantId, first.dataset.variantTitle, first);
        return;
      }
      const select = unitEl.querySelector('[data-mc-variant-select]');
      if (select && select.options.length > 1) {
        select.selectedIndex = 1;
        const option = select.options[1];
        this.pick(card, Number(select.dataset.unit), option.value, option.textContent.trim(), null);
      }
    });
  }

  select(card) {
    if (!card) return;
    this.current = card;

    this.cards.forEach((item) => {
      const active = item === card;
      item.classList.toggle('is-selected', active);
      item.querySelector('[data-mc-select]')?.setAttribute('aria-checked', active ? 'true' : 'false');
      const tones = item.querySelector('[data-mc-tones]');
      if (tones) tones.hidden = !active;
    });

    this.renderPrice(card);
    this.clearError();
    this.refreshButtons();
  }

  pick(card, unit, variantId, variantTitle, swatchEl) {
    const picks = this.picks.get(card.dataset.mcOption);
    if (!picks) return;

    picks[unit] = variantId ? { id: variantId, title: variantTitle } : null;

    const unitEl = card.querySelector(`[data-mc-unit="${unit}"]`);
    if (unitEl) {
      const valueEl = unitEl.querySelector('[data-mc-unit-value]');
      if (valueEl && variantTitle) valueEl.textContent = variantTitle;
      unitEl.querySelectorAll('[data-mc-swatch]').forEach((swatch) => {
        swatch.classList.toggle('is-selected', swatch === swatchEl);
      });
    }

    if (variantTitle) this.filterGallery(variantTitle);

    this.clearError();
    this.refreshButtons();
  }

  renderPrice(card) {
    if (this.priceNow) this.priceNow.textContent = card.dataset.price || '';

    if (this.priceCompare) {
      const compare = card.dataset.compare || '';
      this.priceCompare.textContent = compare;
      this.priceCompare.hidden = compare === '';
    }

    if (this.priceOff) {
      const off = card.dataset.off || '';
      this.priceOff.textContent = off;
      this.priceOff.hidden = off === '';
    }

    if (this.priceUnit) {
      const unit = card.dataset.unitText || '';
      this.priceUnit.textContent = unit;
      this.priceUnit.hidden = unit === '';
    }
  }

  isComplete(card) {
    const picks = this.picks.get(card.dataset.mcOption) || [];
    return picks.every((pick) => pick !== null);
  }

  refreshButtons() {
    if (this.busy || !this.current) return;
    const ready = this.isComplete(this.current);

    if (this.addBtn) this.addBtn.disabled = !ready;
    if (this.buyBtn) this.buyBtn.disabled = !ready;
    if (this.addText) this.addText.textContent = ready ? this.labels.add : this.labels.incomplete;
    if (this.buyText) this.buyText.textContent = ready ? this.labels.buy : this.labels.incomplete;
  }

  /** Agrupa variantes repetidas para não enviar o mesmo id duas vezes. */
  buildItems(card) {
    const totals = new Map();
    const add = (id, quantity) => {
      if (!id) return;
      const key = String(id);
      totals.set(key, (totals.get(key) || 0) + quantity);
    };

    const picks = this.picks.get(card.dataset.mcOption) || [];

    if (picks.length) {
      picks.forEach((pick) => add(pick?.id, 1));
    } else {
      add(card.dataset.variantId, Number(card.dataset.qty || 1));
    }

    if (card.dataset.extraId) {
      add(card.dataset.extraId, Number(card.dataset.extraQty || 1));
    }

    return Array.from(totals, ([id, quantity]) => ({ id, quantity }));
  }

  async submit(goToCheckout) {
    const card = this.current;
    if (!card || this.busy || !this.isComplete(card)) return;

    const items = this.buildItems(card);
    if (!items.length) return;

    this.busy = true;
    this.clearError();
    this.setBusyLabels();

    const body = new FormData();
    items.forEach((item, index) => {
      body.append(`items[${index}][id]`, item.id);
      body.append(`items[${index}][quantity]`, String(item.quantity));
      if (this.propertyName) {
        body.append(`items[${index}][properties][${this.propertyName}]`, card.dataset.title || '');
      }
    });

    try {
      const response = await fetch(this.addUrl, { method: 'POST', body });
      const data = await response.json();
      if (!response.ok || data.status || data.description) throw data;

      if (goToCheckout) {
        window.location.href = `${this.cartUrl}/checkout`;
        return;
      }

      let cart = null;
      try {
        cart = await fetch('/cart.js').then((res) => res.json());
      } catch (error) {
        console.error('[multi-compra] não foi possível reler o carrinho', error);
      }

      this.dispatchEvent(
        new CartAddEvent(cart ?? undefined, this.id || 'multi-compra', {
          source: 'multi-compra',
          itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
        })
      );

      this.flashSuccess();
    } catch (error) {
      console.error('[multi-compra]', error);
      this.showError(
        error?.description ||
          error?.message ||
          'Não foi possível adicionar ao carrinho. Verifique o estoque dos tons escolhidos.'
      );
    }
  }

  setBusyLabels() {
    if (this.addBtn) this.addBtn.disabled = true;
    if (this.buyBtn) this.buyBtn.disabled = true;
    if (this.addText) this.addText.textContent = this.labels.loading;
    if (this.buyText) this.buyText.textContent = this.labels.loading;
  }

  flashSuccess() {
    if (this.addText) this.addText.textContent = this.labels.added;
    if (this.buyText) this.buyText.textContent = this.labels.added;
    setTimeout(() => {
      this.busy = false;
      this.refreshButtons();
    }, 1800);
  }

  showError(message) {
    this.busy = false;
    if (this.errorEl) {
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }
    this.refreshButtons();
  }

  clearError() {
    if (this.errorEl) {
      this.errorEl.textContent = '';
      this.errorEl.hidden = true;
    }
  }
}

if (!customElements.get('multi-compra')) {
  customElements.define('multi-compra', MultiCompra);
}